/********************************************************************************
 * Copyright (c) 2021 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import { graphManager } from '../../GraphManager';
import * as Actions from '../../actions/actions';

const { Notification, NotificationCenter } = JSG;

const sendNotification = (command, type, commandStack) => {
	// eslint-disable-next-line
	NotificationCenter.getInstance().send(
		new Notification(commandStack.STACK_CHANGED_NOTIFICATION, { command, type, commandStack })
	);
};

const sendCommand = (cmd, request, type, onError) => {
	Actions.sendCommand(request)
		.then((response) => cmd.handleResult((response.machineserver && response.machineserver.result) || {}, type))
		.catch((err) => onError(err, type))
		.finally(() => {
			cmd.done({ type, viewer: graphManager.getGraphViewer() });
			graphManager.redraw();
		});
};
const isRequest = (cmd) => cmd && cmd.isRequest;
const popUndo = (stack) => (cmd) => (err, type) => {
	const oldsize = stack.undostack.length;
	stack.undostack.pop();
	if (stack.undostack.length !== oldsize) sendNotification(cmd, type, stack);
};
const popRedo = (stack) => (cmd) => (err, type) => {
	const oldsize = stack.redostack.length;
	stack.redostack.pop();
	if (stack.redostack.length !== oldsize) sendNotification(cmd, type, stack);
};
const handleError =
	(...fn) =>
	(err, type) => {
		fn.forEach((f) => f(err, type));
	};

const RequestCommandStack = (BaseStack) =>
	class extends BaseStack {
		constructor() {
			super();
			this.popRedo = popRedo(this);
			this.popUndo = popUndo(this);
		}

		execute(cmd) {
			super.execute(cmd);
			if (isRequest(cmd)) {
				const req = cmd.getExecuteRequest();
				if (req) sendCommand(cmd, req, 'execute', handleError(cmd.handleError.bind(cmd), this.popUndo(cmd)));
			}
		}
		redo() {
			const cmd = super.redo();
			const req = isRequest(cmd) && cmd.getRedoRequest();
			if (req) sendCommand(cmd, req, 'redo', handleError(cmd.handleError.bind(cmd), this.popUndo(cmd)));
			return cmd;
		}

		undo() {
			const cmd = super.undo();
			const req = isRequest(cmd) && cmd.getUndoRequest();
			if (req) sendCommand(cmd, req, 'undo', handleError(cmd.handleError.bind(cmd), this.popRedo(cmd)));
			return cmd;
		}
	};

export default RequestCommandStack;
